import type { ComponentMaterialGroup } from "@stryker/core/types/jobs";
import { type ChangeEvent, Fragment, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

import type { SingleComponentData } from "../types";

type FieldErrors = {
  component_nps?: string;
  component_schedule?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
};

type TeeComponentProps = {
  components_data: SingleComponentData[];
  set_components_data: React.Dispatch<React.SetStateAction<SingleComponentData[]>>;
  component_material: number | null;
  setComponent_material: React.Dispatch<React.SetStateAction<number | null>>;
  materialGroups: ComponentMaterialGroup[];
  materialError?: string | null;
  componentErrors?: FieldErrors[];
};

const TeeComponent = ({
  components_data,
  set_components_data,
  component_material,
  setComponent_material,
  materialGroups,
  materialError,
  componentErrors,
}: TeeComponentProps) => {
  const { npsOptions, getScheduleOptions, measureUnitOptions } = useConfigOptions();

  // Ensure we have exactly 2 components for tee repair type
  useEffect(() => {
    if (components_data.length !== 2) {
      const emptyInitialData: SingleComponentData = {
        component_nps: null,
        component_diameter: null,
        unit_for_component_diameter: "Inches",
        component_thickness: null,
        unit_for_component_thickness: "Inches",
        component_schedule: null,
      };

      if (components_data.length < 2) {
        // Add second component if missing
        set_components_data([components_data[0], emptyInitialData]);
      } else if (components_data.length > 2) {
        // Trim to 2 components if there are more
        set_components_data(components_data.slice(0, 2));
      }
    }
  }, [components_data.length, set_components_data]);

  const handleChangeData = (value: unknown, place: string, index: number) =>
    set_components_data((prevState) => {
      return prevState.map((p, i) => {
        if (i === index) {
          return {
            ...p,
            [place]: value,
          };
        }
        return p;
      });
    });

  !npsOptions.some((d) => d.title === "custom") && npsOptions.unshift({ title: "custom", value: "custom" });

  const fieldWidth = "w-full sm:w-[270px]";

  return (
    <div className="flex flex-col">
      <Label className="mb-1">
        Component Material <span className="text-red-500">*</span>
      </Label>
      <div className={fieldWidth}>
        <Select
          value={component_material !== null ? String(component_material) : undefined}
          onValueChange={(option) => {
            setComponent_material(Number(option));
          }}
        >
          <SelectTrigger
            className={cn(
              "w-full",
              !component_material && "[&>span]:text-gray-400",
              materialError && "border-error-300"
            )}
          >
            <SelectValue placeholder="Component Material" className="text-red-950" />
          </SelectTrigger>
          <SelectContent>
            {materialGroups.map((group, groupIndex) => (
              <Fragment key={group.group}>
                {groupIndex > 0 && <SelectSeparator />}
                <SelectGroup>
                  <SelectLabel>{group.group}</SelectLabel>
                  {group.options.map((option) => {
                    const descriptor = option.type ?? option.general_material;
                    const label = descriptor ? `${option.material} - ${descriptor}` : option.material;
                    return (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </Fragment>
            ))}
          </SelectContent>
        </Select>
      </div>
      {materialError ? (
        <Typography className="mt-1 text-error-500" variant="text-small">
          {materialError}
        </Typography>
      ) : null}

      {components_data.map((component, index) => {
        const scheduleOptions = component.component_nps !== "custom" ? getScheduleOptions(component.component_nps) : [];

        return (
          <div key={index} className="my-2 flex flex-wrap gap-4 border-b border-solid border-b-gray-200 pb-2">
            <div className="my-2 flex w-full items-end gap-4">
              <Typography variant="display-xs" weight="semibold">
                Component {index + 1} {index === 0 ? "(Header)" : "(Riser)"}
              </Typography>
            </div>

            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1">{`NPS ${index + 1}`}</Label>
              <Select
                value={component.component_nps ?? undefined}
                onValueChange={(option) => {
                  handleChangeData(option, "component_nps", index);
                  if (option === "custom") {
                    // Clear schedule if switching to custom NPS
                    handleChangeData(null, "component_schedule", index);
                  } else {
                    // Clear custom diameter fields when switching away from custom
                    handleChangeData(null, "component_diameter", index);
                    handleChangeData(null, "unit_for_component_diameter", index);
                  }
                }}
              >
                <SelectTrigger
                  className={cn(
                    "w-full",
                    !component.component_nps && "[&>span]:text-gray-400",
                    componentErrors?.[index]?.component_nps && "border-error-300"
                  )}
                >
                  <SelectValue placeholder={`NPS ${index + 1}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{`NPS ${index + 1}`}</SelectLabel>
                    {npsOptions.map((option) => {
                      return (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.title}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {componentErrors?.[index]?.component_nps ? (
                <Typography className="mt-1 text-error-500" variant="text-small">
                  {componentErrors[index]!.component_nps}
                </Typography>
              ) : null}
            </div>

            {component.component_nps === "custom" ? (
              <>
                <TextInput
                  className={fieldWidth}
                  label={`Custom Diameter ${index + 1}`}
                  type="number"
                  name="component_diameter"
                  step={inputStep}
                  required={true}
                  max={maxInputNumber}
                  value={component.component_diameter ?? ""}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleChangeData(event.target.value, "component_diameter", index);
                  }}
                  error={componentErrors?.[index]?.component_diameter}
                />
                <div className={cn("flex flex-col", fieldWidth)}>
                  <Label className="mb-1">Unit</Label>
                  <Select
                    value={component.unit_for_component_diameter ?? undefined}
                    onValueChange={(option) => {
                      handleChangeData(option, "unit_for_component_diameter", index);
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        "w-full",
                        !component.unit_for_component_diameter && "[&>span]:text-gray-400",
                        componentErrors?.[index]?.unit_for_component_diameter && "border-error-300"
                      )}
                    >
                      <SelectValue placeholder={`Unit for Custom Diameter ${index + 1}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>{`Unit for Custom Diameter ${index + 1}`}</SelectLabel>
                        {measureUnitOptions.map((option) => {
                          return (
                            <SelectItem key={option.value} value={String(option.value)}>
                              {option.title}
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {componentErrors?.[index]?.unit_for_component_diameter ? (
                    <Typography className="mt-1 text-error-500" variant="text-small">
                      {componentErrors[index]!.unit_for_component_diameter}
                    </Typography>
                  ) : null}
                </div>
              </>
            ) : null}

            {component.component_nps !== "custom" ? (
              <div className={cn("flex flex-col", fieldWidth)}>
                <Label className="mb-1">{`Schedule ${index + 1}`}</Label>
                <Select
                  value={component.component_schedule ? String(component.component_schedule) : undefined}
                  onValueChange={(option) => {
                    handleChangeData(option, "component_schedule", index);
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      "w-full",
                      !component.component_schedule && "[&>span]:text-gray-400",
                      componentErrors?.[index]?.component_schedule && "border-error-300"
                    )}
                  >
                    <SelectValue placeholder={`Schedule ${index + 1}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>{`Schedule ${index + 1}`}</SelectLabel>
                      {scheduleOptions.map((option) => {
                        return (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.title}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {componentErrors?.[index]?.component_schedule ? (
                  <Typography className="mt-1 text-error-500" variant="text-small">
                    {componentErrors[index]!.component_schedule}
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default TeeComponent;
